// src/utils/pageMeta.js
import { useEffect } from 'react';
import { useBranding } from '../contexts/BrandingContext';

// Sets the document title and meta description for a page. Client-rendered
// SEO: Google executes JS, so per-page titles/descriptions set this way are
// picked up for indexing and search-result snippets.
//
// Callers write {club}, {facility} and {address} rather than the club's actual
// names, and this fills them in from settings/branding. Doing it here rather
// than in each page keeps useBranding out of nine files that otherwise have no
// use for it.
//
// The club name is a token rather than a suffix appended automatically,
// because position varies: most pages read "Something | Club", while the
// landing page leads with the club and qualifies it afterwards.
export function usePageMeta(title, description) {
  const branding = useBranding();
  const fill = (s) => String(s || '')
    .replace(/\{club\}/g, branding.clubName)
    .replace(/\{facility\}/g, branding.facilityName)
    .replace(/\{address\}/g, branding.facilityAddress);

  const resolvedTitle = fill(title);
  const resolvedDescription = fill(description);

  useEffect(() => {
    if (resolvedTitle) document.title = resolvedTitle;
    if (resolvedDescription) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.name = 'description';
        document.head.appendChild(m);
      }
      m.setAttribute('content', resolvedDescription);
    }
    // Resolved values, not the raw arguments: branding arrives from Firestore
    // after the first render, and the title has to follow it.
  }, [resolvedTitle, resolvedDescription]);
}
